import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';

import moment from '~/Providers/Moment';
import history from '~/Providers/History';
import config from '~/Config';
import MyAccountSection from '~/Components/Pages/Account/MyAccountSection';
import { DATE_TIME_FORMAT } from '~/Redux/Constants';
import Spinner from '~/Components/Global/Loading/Spinner';
import { getUser } from '~/Redux/Modules/User';
import { unsubscribe } from '~/Redux/Modules/Subscription';
import { closeModal } from '~/Redux/Modules/Modal';
import { MY_ACCOUNT_MODAL } from '~/Config/Modals';
import { humanDateFormats, HUMAN_DATE_FORMAT } from '~/Redux/Modules/UserOptions';
import StripePermittedView from '~/Components/Global/StripePermittedView';
import { handleGet } from '~/Redux/Modules/Account';
import { isLifetimePlan, getPlanFromIdentifier } from '~/Config/Stripe';
import {
  selectSubscriptionProvider,
  selectUpdatePaymentUrl,
  PLATFORM_PADDLE,
  PADDLE_HELP_URL,
} from '~/util';
import ManageSubscriptionSection from './ManageSubscriptionSection';
import SwapToStripeSection from './SwapToStripeSection';

const stripe = loadStripe(config.stripeKey, config.stripeOptions);

const SUMMARY = 'summary';
const PAYMENTS = 'payments';
const UPDATE_CARD = 'update_card';

export class PaymentsAndBilling extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentView: SUMMARY,
      editingPaymentMethod: null,
    };
  }

  componentWillUnmount = () => {
    if (this.state.editPaymentWindowTimer) {
      clearInterval(this.state.editPaymentWindowTimer);
    }
  };

  handleSetupIntent = () => {
    // Stripe-only. For a Paddle user this would write a card to Stripe and
    // report success while their Paddle subscription stayed on the dead card.
    if (this.props.subscriptionProvider === PLATFORM_PADDLE) return;

    const editPaymentWindow = window.open(
      `${window.location.origin}/change-payment-source`,
      '_blank',
      'popup,width=500,height=600'
    );

    const editPaymentWindowTimer = setInterval(() => {
      if (editPaymentWindow.closed) {
        this.setState({ ...this.state, editingPaymentMethod: null, editPaymentWindowTimer: null });
        clearInterval(editPaymentWindowTimer);
        this.props.fetchAccount();
        this.props.fetchUser();
      }
    }, 100);

    this.setState({
      ...this.state,
      editingPaymentMethod: editPaymentWindow,
      editPaymentWindowTimer,
    });
  };

  renderSpinner = () => {
    return <Spinner />;
  };

  cancelSubscription = () => {
    const { unsubscribe } = this.props;
    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      unsubscribe();
    }
  };

  openSubscriptions = () => {
    const { closeModal } = this.props;
    closeModal();
    history.push('/subscription');
  };

  renderTrialInfo = () => {
    const { account, humanDateFormats } = this.props;
    if (!account.isOnTrial) return null;

    let trialEndsAt = null;
    if (account.trial_ends_at) {
      trialEndsAt = moment(account.trial_ends_at, DATE_TIME_FORMAT).format(
        humanDateFormats[HUMAN_DATE_FORMAT]
      );
    } else if (
      account.stripeSubscription.status === 'trialing' &&
      account.stripeSubscription.current_billing_period?.ends_at
    ) {
      trialEndsAt = moment(account.stripeSubscription.current_billing_period.ends_at).format(
        humanDateFormats[HUMAN_DATE_FORMAT]
      );
    }

    return (
      <div className="payments">
        <section className="payments__trial-period">TRIAL ENDS {trialEndsAt}</section>
        <StripePermittedView>
          {!account.stripeSubscription && !account.iapSubscription && (
            <section className="payments__trial-select">
              <a onClick={this.openSubscriptions}>CHOOSE PLAN</a>
            </section>
          )}
        </StripePermittedView>
      </div>
    );
  };

  renderStripePlan = () => {
    const { account, humanDateFormats } = this.props;
    if (!account.stripeSubscription) return null;
    const activePlan = getPlanFromIdentifier(account.subscriptionType);
    const isLifetime = activePlan?.isLifetime;

    let firstBillingDate = null;
    if (account.stripeSubscription.billing_cycle_anchor) {
      firstBillingDate = moment
        .unix(account.stripeSubscription.billing_cycle_anchor)
        .format(humanDateFormats[HUMAN_DATE_FORMAT]);
    } else if (account.stripeSubscription.first_billed_at) {
      firstBillingDate = moment(account.stripeSubscription.first_billed_at).format(
        humanDateFormats[HUMAN_DATE_FORMAT]
      );
    }

    let nextBillingDate = null;
    if (account.stripeSubscription.next_billed_at) {
      nextBillingDate = moment(account.stripeSubscription.next_billed_at).format(
        humanDateFormats[HUMAN_DATE_FORMAT]
      );
    } else if (account.stripeUpcomingInvoice?.date) {
      nextBillingDate = moment
        .unix(account.stripeUpcomingInvoice.date)
        .format(humanDateFormats[HUMAN_DATE_FORMAT]);
    }

    let nextBillingAmount = '0.00';
    if (account.stripeUpcomingInvoice?.amount_due) {
      nextBillingAmount = (account.stripeUpcomingInvoice.amount_due / 100).toFixed(2);
    } else if (account.stripeSubscription?.next_transaction?.details?.totals?.grand_total) {
      nextBillingAmount = (
        account.stripeSubscription.next_transaction.details.totals.grand_total / 100
      ).toFixed(2);
    }

    return (
      <div className="payments">
        <section className="payments__current-plan">
          <div className="payments__current-plan__title">Current Plan</div>
          <div className="payments__current-plan__value">{activePlan?.displayName}</div>
        </section>
        <section className="payments__first-billing-date">
          <div className="payments__first-billing-date__title">First Billing Date</div>
          <div className="payments__first-billing-date__value">{firstBillingDate}</div>
        </section>
        <section className="payments__next-billing-date">
          <div className="payments__next-billing-date__title">Next Billing Date</div>
          <div className="payments__next-billing-date__value">
            {isLifetime ? 'Lifetime' : nextBillingDate}
          </div>
        </section>
        {!isLifetime && (
          <section className="payments__next-billing-amount">
            <div className="payments__next-billing-amount__title">Next Billing Amount</div>
            <div className="payments__next-billing-amount__value">${nextBillingAmount}</div>
          </section>
        )}
      </div>
    );
  };

  renderIapPlan = () => {
    const { account, humanDateFormats } = this.props;
    if (!account.iapSubscription) return null;
    return (
      <div className="payments">
        <section className="payments__current-plan">
          <div className="payments__current-plan__title">Platform</div>
          <div className="payments__current-plan__value">
            {account.iapSubscription.platform.toUpperCase()}
          </div>
        </section>
        <section className="payments__first-billing-date">
          <div className="payments__first-billing-date__title">Current Plan</div>
          <div className="payments__first-billing-date__value">
            {account.iapSubscription.sku.toUpperCase()}
          </div>
        </section>
        <section className="payments__next-billing-date">
          <div className="payments__next-billing-date__title">Next Billing Date</div>
          <div className="payments__next-billing-date__value">
            {moment(account.iapSubscription.ends_at, DATE_TIME_FORMAT).format(
              humanDateFormats[HUMAN_DATE_FORMAT]
            )}
          </div>
        </section>
      </div>
    );
  };

  renderUpdateAndPaymentHistoryLinks = () => {
    const { account, subscriptionProvider, paddleUpdatePaymentUrl } = this.props;
    if (!account.stripeSubscription) return null;

    // Branch on the provider, not on the URL. A failed Paddle fetch serves an
    // empty object here, which is truthy, and used to fall through to Stripe.
    if (subscriptionProvider === PLATFORM_PADDLE) {
      return (
        <>
          {paddleUpdatePaymentUrl && (
            <a href={paddleUpdatePaymentUrl} target="_blank" rel="noopener noreferrer">
              Payment Information &amp; History
            </a>
          )}
          <a href={PADDLE_HELP_URL} target="_blank" rel="noopener noreferrer">
            Help With My Subscription &amp; Payments
          </a>
        </>
      );
    }

    return (
      <>
        <a onClick={() => this.setState({ ...this.state, currentView: UPDATE_CARD })}>
          Update Payment Information
        </a>
        <a onClick={() => this.setState({ ...this.state, currentView: PAYMENTS })}>
          View Payment History
        </a>
      </>
    );
  };

  renderSummary = () => {
    const { account, openCancellation } = this.props;
    const activePlan = getPlanFromIdentifier(account.subscriptionType);
    const isLifetime = activePlan?.isLifetime;

    let cancelled = false;
    if (account.stripeSubscription && account.isOnGracePeriod) {
      cancelled = true;
    } else if (account.stripeSubscription?.scheduled_change?.action === 'cancel') {
      cancelled = true;
    }

    return (
      <div className="payments-and-billing">
        {this.renderTrialInfo()}
        {this.renderStripePlan()}
        {this.renderIapPlan()}
        <StripePermittedView>
          <div className="links">
            {account.stripeSubscription && !account.isOnGracePeriod && !isLifetime && (
              <ManageSubscriptionSection />
            )}
            {account.iapSubscription && !account.stripeSubscription && <SwapToStripeSection />}
            {this.renderUpdateAndPaymentHistoryLinks()}
            {!isLifetime && (
              <>
                {account.stripeSubscription ? (
                  cancelled ? (
                    'SUBSCRIPTION CANCELLED'
                  ) : (
                    <a onClick={openCancellation}>Cancel Subscription</a>
                  )
                ) : null}
              </>
            )}
          </div>
        </StripePermittedView>
      </div>
    );
  };

  renderCharge = ({ amount, created, status }) => {
    const { humanDateFormats } = this.props;
    return (
      <section className="payment-detail__charge">
        <div className="payment-detail__charge__created">
          {moment.unix(created).format(humanDateFormats[HUMAN_DATE_FORMAT])}
        </div>
        <div className="payment-detail__charge__amount">${amount / 100}</div>
        <div className="payment-detail__charge__status">{status}</div>
      </section>
    );
  };

  renderPayments = () => {
    const {
      account: { stripeCharges },
    } = this.props;
    return (
      <div className="payments-and-billing">
        <div className="payment-detail">
          <section className="payment-detail__heading">
            <div className="payment-detail__heading__label">Payment History</div>
            <a
              className="payment-detail__heading__back"
              onClick={() => this.setState({ ...this.state, currentView: SUMMARY })}
            >
              Back to Summary
            </a>
          </section>
          {stripeCharges.map(charge => this.renderCharge(charge))}
        </div>
      </div>
    );
  };

  renderUpdateCard = () => {
    const { user } = this.props;
    return (
      <div className="payments-and-billing">
        <div className="update-card">
          <section className="update-card__heading">
            <div className="update-card__heading__label">Update Payment Method</div>
            <a
              className="update-card__heading__back"
              onClick={() => this.setState({ ...this.state, currentView: SUMMARY })}
            >
              Back to Summary
            </a>
          </section>
          <section className="update-card__current">
            <div className="update-card__current__label">Current Card:</div>
            <div className="update-card__current__brand">{user.card_brand ?? user.pm_type}</div>
            <div className="update-card__current__last4">
              {user.card_last_four ?? user.pm_last_four}
            </div>
          </section>

          <section className="update-card__new">
            {this.state.editingPaymentMethod ? (
              <Spinner />
            ) : (
              <a
                className="update-card__new__open"
                onClick={() => this.handleSetupIntent()}
                role="button"
                tabIndex="0"
              >
                Update...
              </a>
            )}
          </section>
        </div>
      </div>
    );
  };

  renderBody = () => {
    const { currentView } = this.state;
    switch (currentView) {
      case SUMMARY:
        return this.renderSummary();
      case PAYMENTS:
        return this.renderPayments();
      case UPDATE_CARD:
        return this.renderUpdateCard();
    }
  };

  render() {
    const { account, isFetching } = this.props;

    return (
      <MyAccountSection header={'Payments & Billing'}>
        {isFetching || !account.id ? this.renderSpinner() : this.renderBody()}
      </MyAccountSection>
    );
  }
}

PaymentsAndBilling.propTypes = {
  unsubscribe: PropTypes.func,
  closeModal: PropTypes.func,
  account: PropTypes.object,
  openCancellation: PropTypes.func,
  user: PropTypes.object,
  isUpdating: PropTypes.bool,
  isFetching: PropTypes.bool,
  humanDateFormats: PropTypes.object.isRequired,
  fetchAccount: PropTypes.func.isRequired,
  fetchUser: PropTypes.func.isRequired,
  subscriptionProvider: PropTypes.string,
  paddleUpdatePaymentUrl: PropTypes.string,
};

const mapStateToProps = ({ Account, User, UserOptions }) => {
  return {
    account: Account.data,
    isFetching: Account.isFetching,
    isUpdating: User.isUpdating,
    user: User.data,
    humanDateFormats: humanDateFormats({ UserOptions }),
    subscriptionProvider: selectSubscriptionProvider({ User }),
    paddleUpdatePaymentUrl: selectUpdatePaymentUrl({ Account }),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    unsubscribe: () => dispatch(unsubscribe()),
    closeModal: () => dispatch(closeModal(MY_ACCOUNT_MODAL)),
    fetchAccount: () => dispatch(handleGet()),
    fetchUser: () => dispatch(getUser()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentsAndBilling);
